// High-level convenience component
import React, { useMemo, useRef } from 'react';

import { Box, MantineColor, MantineStyleProp } from '@mantine/core';
import { IconDelete, IconGripVertical } from 'ui/Icons';

import { ThemeIcon } from '../../../DataDisplay/ThemeIcon';
import { cx } from '../../../Provider/Provider';
import { DragHandle, DropTarget } from '../../molecules/drag-and-drop/DragAndDropComponents';
import { DragAndDropProvider } from '../../molecules/drag-and-drop/DragAndDropProvider';
import classes from './DragAndDropList.module.css';

interface DragAndDropListItem {
  id: string;
  [key: string]: unknown;
}

interface DragAndDropListProps<T extends DragAndDropListItem> {
  items: T[];
  onReorder: (sourceIndex: number, destIndex: number) => void;
  onDelete?: (itemId: string) => void;
  renderItem: (item: T, index: number) => React.ReactNode;
  // Optional customization
  className?: string;
  style?: MantineStyleProp;
  disabled?: boolean;
  // Drag handle options
  showDragHandle?: boolean;
  dragHandlePosition?: 'left' | 'right';
  customDragHandle?: React.ReactNode;
  // Delete button options
  showDeleteButton?: boolean;
  deleteButtonPosition?: 'left' | 'right';
  customDeleteButton?: React.ReactNode;
  // Visual options
  itemStyle?: MantineStyleProp;
  itemClassName?: MantineStyleProp;
  dropIndicatorColor?: MantineColor;
  dropIndicatorThickness?: number;
  itemGap?: number;
  direction?: 'vertical' | 'horizontal';
  scrollable?: boolean;
  maxHeight?: number | string;
  dropIndicatorStyles?: {
    indicator?: MantineStyleProp;
    dot?: MantineStyleProp;
    line?: MantineStyleProp;
  };
}

const DefaultHandleElement = React.memo(() => (
  <ThemeIcon variant="transparent" color="gray.4" size="sm">
    <IconGripVertical size={18} brand="tabler" />
  </ThemeIcon>
));
DefaultHandleElement.displayName = 'DefaultHandleElement';

interface DefaultDeleteElementProps {
  onDelete?: (itemId: string) => void;
  itemId: string;
  index: number;
  disabled: boolean;
}

const DefaultDeleteElement = React.memo<DefaultDeleteElementProps>(
  ({ onDelete, itemId, index, disabled }) => (
    <ThemeIcon
      onClick={() => (disabled ? null : onDelete?.(itemId))}
      variant="transparent"
      color="cerise.7"
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      aria-label={`Delete item ${index + 1}`}
    >
      <IconDelete size={18} />
    </ThemeIcon>
  )
);
DefaultDeleteElement.displayName = 'DefaultDeleteElement';

interface PreviewElementProps<T extends DragAndDropListItem> {
  item: T;
  index: number;
  renderContent: (item: T, index: number) => React.ReactNode;
  showDragHandle: boolean;
  dragHandlePosition: 'left' | 'right';
  handleElement: React.ReactNode;
}

function PreviewElementComponent<T extends DragAndDropListItem>({
  item,
  index,
  renderContent,
  showDragHandle,
  dragHandlePosition,
  handleElement,
}: PreviewElementProps<T>) {
  const showHandleOnLeft = showDragHandle && dragHandlePosition === 'left';
  return (
    <div className={classes.DragAndDropList__Preview}>
      {showHandleOnLeft && handleElement}
      <div className={classes.DragAndDropList__PreviewContent}>{renderContent(item, index)}</div>
    </div>
  );
}

const PreviewElement = React.memo(PreviewElementComponent) as typeof PreviewElementComponent;

/**
 * High-level drag-and-drop list with all batteries included
 */
function DragAndDropList<T extends DragAndDropListItem>({
  items,
  onReorder,
  onDelete,
  renderItem,
  disabled = false,
  showDragHandle = true,
  dragHandlePosition = 'left',
  customDragHandle,
  showDeleteButton = false,
  deleteButtonPosition = 'right',
  customDeleteButton,
  itemStyle,
  itemClassName,
  dropIndicatorColor,
  dropIndicatorThickness,
  itemGap = 0,
  direction = 'vertical',
  scrollable = false,
  maxHeight,
  dropIndicatorStyles,
  style,
  className,
}: DragAndDropListProps<T>) {
  return (
    <DragAndDropProvider
      onReorder={onReorder}
      disabled={disabled}
      direction={direction}
      className={cx(classes.DragAndDropList, className)}
      style={{
        gap: itemGap, // Let CSS handle the rest
        maxHeight:
          scrollable && maxHeight
            ? typeof maxHeight === 'number'
              ? `${maxHeight}px`
              : maxHeight
            : undefined,
        ...style,
      }}
      data-direction={direction}
      data-scrollable={scrollable}
    >
      {items.map((item, index) => (
        <DragAndDropListItem
          key={item.id}
          item={item}
          index={index}
          renderContent={renderItem}
          showDragHandle={showDragHandle}
          dragHandlePosition={dragHandlePosition}
          customDragHandle={customDragHandle}
          showDeleteButton={showDeleteButton}
          deleteButtonPosition={deleteButtonPosition}
          customDeleteButton={customDeleteButton}
          onDelete={onDelete}
          itemStyle={itemStyle}
          itemClassName={itemClassName}
          dropIndicatorColor={dropIndicatorColor}
          dropIndicatorThickness={dropIndicatorThickness}
          dropIndicatorGap={itemGap}
          dropIndicatorStyles={dropIndicatorStyles}
          disabled={disabled}
        />
      ))}
    </DragAndDropProvider>
  );
}

// Side element component that decides what to render
interface SideElementProps<T extends DragAndDropListItem> {
  side: 'left' | 'right';
  item: T;
  index: number;
  showDragHandle: boolean;
  dragHandlePosition: 'left' | 'right';
  showDeleteButton: boolean;
  deleteButtonPosition: 'left' | 'right';
  handleElement: React.ReactNode;
  deleteElement: React.ReactNode;
  previewElement: React.ReactNode;
  disabled: boolean;
}

const SideElement = React.memo(function SideElement<T extends DragAndDropListItem>({
  side,
  item,
  index,
  showDragHandle,
  dragHandlePosition,
  showDeleteButton,
  deleteButtonPosition,
  handleElement,
  deleteElement,
  previewElement,
  disabled,
}: SideElementProps<T>) {
  const shouldShowHandle = useMemo(
    () => showDragHandle && dragHandlePosition === side,
    [showDragHandle, dragHandlePosition, side]
  );
  const shouldShowDelete = useMemo(
    () => showDeleteButton && deleteButtonPosition === side,
    [showDeleteButton, deleteButtonPosition, side]
  );

  if (shouldShowHandle) {
    return (
      <DragHandle
        itemId={item.id}
        index={index}
        disabled={disabled}
        aria-label={`Drag item ${index + 1}`}
        renderPreview={() => previewElement}
      >
        {handleElement}
      </DragHandle>
    );
  }

  if (shouldShowDelete) {
    return <>{deleteElement}</>;
  }

  return null;
});

SideElement.displayName = 'SideElement';

// Internal component for each draggable item
interface DragAndDropListItemProps<T extends DragAndDropListItem> {
  item: T;
  index: number;
  renderContent: (item: T, index: number) => React.ReactNode;
  showDragHandle: boolean;
  dragHandlePosition: 'left' | 'right';
  customDragHandle?: React.ReactNode;
  showDeleteButton: boolean;
  deleteButtonPosition: 'left' | 'right';
  customDeleteButton?: React.ReactNode;
  onDelete?: (itemId: string) => void;
  itemStyle?: MantineStyleProp;
  itemClassName?: MantineStyleProp;
  dropIndicatorColor?: MantineColor;
  dropIndicatorThickness?: number;
  dropIndicatorGap?: number;
  dropIndicatorStyles?: {
    indicator?: MantineStyleProp;
    dot?: MantineStyleProp;
    line?: MantineStyleProp;
  };
  disabled: boolean;
}

function DragAndDropListItem<T extends DragAndDropListItem>({
  item,
  index,
  renderContent,
  showDragHandle,
  dragHandlePosition,
  customDragHandle,
  showDeleteButton,
  deleteButtonPosition,
  customDeleteButton,
  onDelete,
  itemStyle,
  itemClassName,
  dropIndicatorColor,
  dropIndicatorThickness,
  dropIndicatorGap,
  dropIndicatorStyles,
  disabled,
}: DragAndDropListItemProps<T>) {
  const contentRef = useRef<HTMLDivElement>(null);
  const handleElement = useMemo(
    () => customDragHandle || <DefaultHandleElement />,
    [customDragHandle]
  );

  const deleteElement = useMemo(
    () =>
      customDeleteButton || (
        <DefaultDeleteElement
          onDelete={onDelete}
          itemId={item.id}
          index={index}
          disabled={disabled}
        />
      ),
    [customDeleteButton, onDelete, item.id, index, disabled]
  );

  const previewElement = useMemo(
    () => (
      <PreviewElement
        item={item}
        index={index}
        renderContent={renderContent}
        showDragHandle={showDragHandle}
        dragHandlePosition={dragHandlePosition}
        handleElement={handleElement}
      />
    ),
    [item, index, renderContent, showDragHandle, dragHandlePosition, handleElement]
  );

  return (
    <DropTarget
      itemId={item.id}
      index={index}
      disabled={disabled}
      dropIndicatorColor={dropIndicatorColor}
      dropIndicatorThickness={dropIndicatorThickness}
      dropIndicatorGap={dropIndicatorGap}
      styles={dropIndicatorStyles}
    >
      <Box
        ref={contentRef}
        className={cx(classes.DragAndDropList__Item, itemClassName)}
        style={itemStyle}
      >
        {/* Left side - always rendered, but may return null */}
        {/* Props are explicit for clarity and flexibility*/}
        <Box className={classes.DragAndDropList__SideElement}>
          <SideElement
            side="left"
            item={item}
            index={index}
            showDragHandle={showDragHandle}
            dragHandlePosition={dragHandlePosition}
            showDeleteButton={showDeleteButton}
            deleteButtonPosition={deleteButtonPosition}
            handleElement={handleElement}
            deleteElement={deleteElement}
            previewElement={previewElement}
            disabled={disabled}
          />
        </Box>
        {/* Main content */}
        <Box className={classes.DragAndDropList__Content} data-draggable={!showDragHandle}>
          {!showDragHandle ? (
            <DragHandle
              itemId={item.id}
              index={index}
              disabled={disabled}
              style={{ width: '100%', cursor: 'grab' }}
              renderPreview={() => previewElement}
            >
              {renderContent(item, index)}
            </DragHandle>
          ) : (
            renderContent(item, index)
          )}
        </Box>
        {/* Right side - always rendered, but may return null */}
        <Box className={classes.DragAndDropList__SideElement}>
          <SideElement
            side="right"
            item={item}
            index={index}
            showDragHandle={showDragHandle}
            dragHandlePosition={dragHandlePosition}
            showDeleteButton={showDeleteButton}
            deleteButtonPosition={deleteButtonPosition}
            handleElement={handleElement}
            deleteElement={deleteElement}
            previewElement={previewElement}
            disabled={disabled}
          />
        </Box>
      </Box>
    </DropTarget>
  );
}

export { DragAndDropList };
export type { DragAndDropListItem, DragAndDropListProps };
